import * as React from "react";
import { cn } from "@/shared/lib/utils";
import { Checkbox as BaseCheckbox } from "@/shared/ui/components/ui/checkbox";

export interface CheckboxProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  label?: string;
  helper?: string;
  description?: string;
  error?: boolean;
  variant?: "default" | "advanced";
  size?: "sm" | "xl" | "2xl";
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof BaseCheckbox>,
  CheckboxProps
>(
  (
    {
      checked = false,
      onCheckedChange,
      disabled,
      contentClassName,
      className,
      label,
      helper,
      description,
      error,
      variant = "default",
      size = "sm",
    },
    ref
  ) => {
    const hasError = Boolean(error);

    const defaultCheckboxStyles = cn(
      "h-4 w-4 sm:h-5 sm:w-5 rounded-[2px] border transition-colors",
      "!border-grey-200",
      "bg-white",
      "hover:!border-grey-500 disabled:hover:!border-grey-200",
      "data-[state=checked]:bg-brand-green data-[state=checked]:!border-brand-green data-[state=checked]:text-white",
      "disabled:bg-grey-50 disabled:!border-grey-100",
      "disabled:data-[state=checked]:bg-green-300 disabled:data-[state=checked]:!border-green-300 disabled:data-[state=checked]:text-white",
      hasError &&
      !disabled &&
      !checked &&
      "!border-error-700 hover:!border-error-700"
    );

    const advancedContainerStyles = cn(
      "flex items-center gap-2 p-3 border transition-colors",
      size === "sm" && "rounded-lg",
      size === "2xl" && "rounded-lg w-[300px] sm:w-[589px] xl:w-[420px] 2xl:w-[540px] justify-start items-start",
      size === "xl" && "rounded-lg",
      "bg-grey-50 border-grey-100",
      !disabled && !checked && "hover:bg-grey-100",
      "data-[checked=true]:bg-green-100 data-[checked=true]:border-brand-green",
      "disabled:data-[checked=true]:bg-green-50 disabled:data-[checked=true]:border-green-200 disabled:data-[checked=true]:hover:bg-green-50 disabled:data-[checked=true]:hover:border-green-200",
      "disabled:data-[checked=false]:bg-grey-50 disabled:data-[checked=false]:border-grey-200 disabled:hover:bg-grey-100",
      hasError && !disabled && !checked && "!bg-error-bg !border-error-700"
    );

    const advancedCheckboxStyles = defaultCheckboxStyles;

    if (variant === "advanced") {
      return (
        <div className={cn("inline-flex", className)}>
          <label
            className={cn(
              advancedContainerStyles,
              "cursor-pointer",
              contentClassName, disabled && "cursor-not-allowed",
            )}
            data-checked={checked}
            data-error={hasError || undefined}
          >
            <BaseCheckbox
              ref={ref}
              checked={checked}
              onCheckedChange={onCheckedChange}
              disabled={disabled}
              className={advancedCheckboxStyles}
              data-error={hasError || undefined}
            />
            {label && (
              <div className="flex flex-col gap-3 items-start rtl:items-end">
                <span
                  className={cn(
                    "text-sm font-medium",
                    disabled ? "text-grey-400" : "text-grey-900"
                  )}
                >
                  {label}
                </span>
                {description && (
                  <p className="text-xs text-grey-300 font-medium rtl:text-right">
                    {description}
                  </p>
                )}
              </div>
            )}
          </label>
        </div>
      );
    }

    return (
      <div className={cn("flex items-start gap-2", className)}>
        <BaseCheckbox
          ref={ref}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          className={defaultCheckboxStyles}
          data-error={hasError || undefined}
        />
        {(label || helper) && (
          <div className="flex flex-col gap-2">
            {label && (
              <label
                className={cn(
                  " cursor-pointer",
                  size === "sm"
                    ? " text-xxs "
                    : " text-xs",
                  checked ? "font-semibold" : "font-medium",
                  hasError && !disabled && !checked && "text-error-700",
                  disabled ? "text-grey-200" : !hasError && "text-black"
                )}
                onClick={() => !disabled && onCheckedChange?.(!checked)}
              >
                {label}
              </label>
            )}
            {helper && (
              <p
                className={cn(
                  "text-xxs",
                  size === "sm" ? "font-medium" : "font-bold",
                  disabled ? "text-grey-200" : !hasError && "text-grey-400"
                )}
              >
                {helper}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
